// src/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useTheme } from '../theme';
import SEO, { SEO_CONFIG } from '../components/SEO';


// Deliberate narrow auth-card frame — not the dashboard frame scale
// (--frame-dashboard / --frame-prose). Named so the intent is documented once
// instead of repeating a bare literal.
const LOGIN_CARD_MAX_WIDTH = '400px'; // logged-out sign-in card

// Module-level, not component state: Layout.jsx's `hideChrome` depends on
// `isLoginPage && !user`, so the instant a sign-in succeeds and `user`
// becomes truthy while still sitting on /login, Layout switches from its
// bare-children render branch to its full-shell branch. Those are two
// structurally different subtrees, so React unmounts and remounts
// everything inside — including this component — wiping any local state or
// ref an in-flight sign-in had set (role picker resets to its default,
// any "I already know where I'm going" flag resets to false) before the
// click handler's own post-await code ever runs. A plain module variable
// lives outside the tree that gets torn down, so it survives that remount;
// the effect below reads it once `user` shows up, regardless of which
// component instance — the original or the one Layout just remounted —
// ends up rendering.
let pendingSignInDestination = null;

export default function Login() {
  const { signInGoogle, signInApple, mode, user } = useAuth();
  const { setThemeOverride } = useTheme();
  const [err, setErr] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('signup') === 'true';
  // RequireAuth renders <Login/> inline (shell visible) on protected routes
  // instead of redirecting to the standalone /login route (shell hidden).
  // The fixed .login-container offsets below must differ accordingly.
  const isStandaloneLogin = location.pathname === '/login';
  // RequireAuth renders <Login/> inline on whatever protected route the user
  // hit while logged out — it never navigates, so location.state.from (the
  // usual react-router redirect pattern) is never set by anything in this
  // app and was always undefined. Since Login is literally rendered AT that
  // route, location.pathname already IS the page to return to — no state
  // needed. Only fall back to the role default on the standalone /login
  // route itself, where pathname is '/login', not a real destination.
  // state.from wins when a caller sets it explicitly (the share page navigates
  // to /login and needs the user back on /shared/{token}). Otherwise Login is
  // rendered AT the protected route, so pathname already is the destination.
  // The final fallback is /home — the resolver that picks the right landing
  // (dashboard / parent portal / paywall) from account state, replacing the
  // old learner/supervisor sign-in toggle.
  const from =
    location.state?.from?.pathname ||
    (location.pathname !== '/login' && location.pathname) ||
    '/home';

  // Force a light appearance for the unauthenticated login/signup page. This
  // is a non-persisting override (see ThemeContext) — it does not touch the
  // user's stored theme preference, so a hard reload while sitting on this
  // page can't overwrite it to 'light' permanently.
  useEffect(() => {
    if (!user) {
      setThemeOverride('light');
      return () => setThemeOverride(null);
    }
  }, [user, setThemeOverride]);

  // Buttons are disabled while `signingIn` is true (see JSX below) so a fast
  // double/triple-click can't fire a second concurrent signInGoogle()/
  // signInApple() call — Firebase cancels an earlier pending popup when a
  // new one is requested, and running two attempts at once produced its own
  // corrupted-state bugs during debugging.
  const [signingIn, setSigningIn] = useState(false);

  const handleGoogleSignIn = async () => {
    if (signingIn) return;
    try {
      setErr('');
      setSigningIn(true);
      // Only the standalone /login page suffers the Layout remount (see
      // pendingSignInDestination above) — setting this for an inline
      // sign-in too would leak a stale destination into a later, unrelated
      // standalone /login visit, since nothing here guarantees the cleanup
      // effect runs before RequireAuth swaps Login out for the real page.
      // Inline sign-ins don't need any navigation call at all: `from`
      // already equals the current route, and RequireAuth handles the
      // transition to the real page itself once `user` updates.
      if (isStandaloneLogin) pendingSignInDestination = from;
      await signInGoogle();
      // Navigation happens in the effect below once `user` updates, not
      // here — see pendingSignInDestination above for why.
    } catch (e) {
      pendingSignInDestination = null;
      setSigningIn(false);
      setErr(String(e?.message || e));
    }
  };

  const handleAppleSignIn = async () => {
    if (signingIn) return;
    try {
      setErr('');
      setSigningIn(true);
      if (isStandaloneLogin) pendingSignInDestination = from;
      await signInApple();
    } catch (e) {
      pendingSignInDestination = null;
      setSigningIn(false);
      setErr(String(e?.message || e));
    }
  };

  // Google "G" logo in standard colors
  const GoogleLogo = () => (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );

  // Apple logo
  const AppleLogo = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );

  // Routes a just-completed sign-in to its destination once `user` updates —
  // see pendingSignInDestination above for why this can't live in the click
  // handler's own post-await code. Falls back to /home (the landing resolver)
  // for a stale /login bookmark visit with no sign-in in flight — but only on
  // the standalone /login route: RequireAuth renders <Login/> inline on
  // protected routes too, and that instance must never redirect itself, or it
  // steals the landing spot from whatever page the caller was already headed
  // to. RequireAuth swaps Login out for the real page on its own once `user`
  // catches up, with no navigation needed.
  useEffect(() => {
    if (!user) return;
    if (pendingSignInDestination) {
      const destination = pendingSignInDestination;
      pendingSignInDestination = null;
      navigate(destination, { replace: true });
    } else if (isStandaloneLogin) {
      // Already-signed-in visit to /login (e.g. a stale bookmark): send them to
      // the landing resolver so they end up on their proper home, not stuck.
      navigate('/home', { replace: true });
    }
  }, [user, isStandaloneLogin, navigate]);

  // All hooks above run first, so this early return never changes hook order.
  if (user) {
    return null;
  }

  // Logged out view
  return (
    <>
    <SEO {...SEO_CONFIG.login} />
    <div className="login-container" style={{
      position: 'fixed',
      top: isStandaloneLogin ? 0 : 'var(--topbar-h)',
      left: isStandaloneLogin ? 0 : 'var(--layout-sidebar-w)',
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-5)',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Desktop Layout */}
      <div className="login-desktop" style={{ width: '100%', maxWidth: LOGIN_CARD_MAX_WIDTH }}>
        {/* Header */}
        <header style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
          <h1 className="login-title" style={{
            /* Deliberately exceeds --text-page-size (27px) — this is the
               highest-impression headline on the app's entry screen. */
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: '800',
            letterSpacing: '-0.02em',
            marginBottom: 'var(--space-3)',
            color: 'var(--text-primary)',
            lineHeight: '1.1',
          }}>
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="login-subtitle" style={{ fontSize: 'var(--text-panel-size)', color: 'var(--text-tertiary)', lineHeight: '1.5' }}>
            {mode === 'mock'
              ? 'Firebase is not configured.'
              : isSignup
                ? 'Create your account to get started'
                : 'Sign in to continue'}
          </p>
        </header>

        {/* Card with gradient border */}
        <div className="login-card-wrapper" style={{
          borderRadius: 'var(--radius-overlay)',
          padding: '1px',
          background: 'linear-gradient(135deg, var(--accent-light) 0%, var(--accent-light) 100%)',
        }}>
          <div className="login-card" style={{
            background: 'var(--bg-secondary)',
            /* 1px less than the wrapper's --radius-overlay so the inset
               border reads as concentric, not off-center. */
            borderRadius: 'calc(var(--radius-overlay) - 1px)',
            padding: 'var(--space-6)',
          }}>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={signingIn}
              className="login-google-btn"
              style={{
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-body-size)',
                fontWeight: '500',
                fontFamily: 'Roboto, sans-serif',
                border: '1px solid var(--border-hover)',
                cursor: signingIn ? 'default' : 'pointer',
                opacity: signingIn ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-3)',
                transition: 'all 0.2s ease',
                marginBottom: 'var(--space-5)',
              }}
              onMouseOver={(e) => {
                if (!signingIn) e.currentTarget.style.background = 'var(--bg-elevated)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
            >
              <GoogleLogo />
              <span>{isSignup ? 'Sign up with Google' : 'Sign in with Google'}</span>
            </button>

            <button
              type="button"
              onClick={handleAppleSignIn}
              disabled={signingIn}
              className="login-apple-btn"
              style={{
                width: '100%',
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-body-size)',
                fontWeight: '500',
                fontFamily: 'Roboto, sans-serif',
                border: '1px solid var(--border-hover)',
                cursor: signingIn ? 'default' : 'pointer',
                opacity: signingIn ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-3)',
                transition: 'all 0.2s ease',
                marginBottom: 'var(--space-5)',
              }}
              onMouseOver={(e) => {
                if (!signingIn) e.currentTarget.style.background = 'var(--bg-elevated)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
            >
              <AppleLogo />
              <span>{isSignup ? 'Sign up with Apple' : 'Sign in with Apple'}</span>
            </button>

            {err && (
              <div style={{
                padding: 'var(--space-3)',
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-control)',
                fontSize: 'var(--text-body-size)',
                color: 'var(--danger)',
                marginBottom: 'var(--space-4)',
              }}>
                {err}
              </div>
            )}

            <div className="login-privacy" style={{ textAlign: 'center', fontSize: 'var(--text-label-size)', color: 'var(--text-tertiary)', lineHeight: '1.5' }}>
              <span style={{ color: 'var(--accent-primary)', fontWeight: '600' }}>Cancel anytime.</span> We only use your email for sync.
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="login-support" style={{
          marginTop: 'var(--space-5)',
          textAlign: 'center',
          fontSize: 'var(--text-label-size)',
          color: 'var(--text-tertiary)',
        }}>
          Need help?{' '}
          <a
            href="mailto:contact@graspd.ai"
            style={{ color: 'var(--text-secondary)', fontWeight: '500' }}
          >
            contact@graspd.ai
          </a>
        </div>
      </div>
    </div>
    <style>{`
      @media (max-width: 768px) {
        .login-container {
          left: 0 !important;
          top: 0 !important;
          padding: 0 !important;
          align-items: stretch !important;
          justify-content: stretch !important;
          background: var(--bg-primary);
        }
        .login-desktop {
          max-width: 100% !important;
          width: 100% !important;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: var(--space-5);
          box-sizing: border-box;
        }
        .login-desktop header {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          margin-bottom: 0 !important;
          padding-top: var(--space-7);
        }
        .login-title {
          /* Exceeds --text-page-size (27px) on purpose — same mobile hero
             role as the desktop clamp() above. */
          font-size: 32px !important;
          margin-bottom: var(--space-2) !important;
        }
        .login-subtitle {
          font-size: var(--text-panel-size) !important;
          color: var(--text-secondary) !important;
        }
        .login-card-wrapper {
          background: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
          margin-top: auto;
          padding-bottom: var(--space-6) !important;
        }
        .login-card {
          background: transparent !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        .login-google-btn,
        .login-apple-btn {
          padding: var(--space-4) var(--space-5) !important;
          font-size: var(--text-panel-size) !important;
          background: var(--bg-elevated) !important;
          border: 1px solid var(--border-hover) !important;
          border-radius: var(--radius-panel) !important;
          margin-bottom: var(--space-3) !important;
        }
        .login-privacy {
          font-size: var(--text-body-size) !important;
          padding-bottom: var(--space-5);
        }
      }
    `}</style>
    </>
  );
}
